import { aquaState } from './aquaStore';
import { pathLength, pipeLatLngs } from './model';
import { innerD } from './aquaNet';

const n1 = (v, d = 1) => (v == null || !Number.isFinite(v) ? '-' : v.toFixed(d));
const nm = (f) => f?.name || `${f?.type} ${String(f?.id || '').slice(-4)}`;

// briefing for DripX: drawn network, hydraulic data and the latest AquaFlow results
export function aquaContext(project) {
  if (!project) return '';
  const fits = project.fittings || [], pipes = project.pipes || [], byId = Object.fromEntries(fits.map((f) => [f.id, f]));
  const dn = {};
  pipes.forEach((p) => { const k = `DN${Math.round(innerD(p))} ${p.material || ''}`.trim(); dn[k] = (dn[k] || 0) + pathLength(pipeLatLngs(p, fits)); });
  const src = fits.filter((f) => f.type === 'source' || f.hyd?.src), vs = fits.filter((f) => f.type === 'valve'), prv = fits.filter((f) => f.type === 'prv');
  const cons = fits.filter((f) => +f.hyd?.q > 0 || f.type === 'house');
  const cfg = project.aqua || {};
  const L = [
    'AQUAFLOW NETWORK (hydraulic model of the open project):',
    `Pipes: ${pipes.length} (${Object.entries(dn).map(([k, m]) => `${k}: ${Math.round(m)} m`).join(', ') || '-'}).`,
    `Supplies: ${src.map((f) => `${nm(f)} ${f.hyd?.bar ?? 4} bar, ground ${f.elev?.v ?? '?'} m`).join('; ') || 'none'}.`,
    `Valves: ${vs.length} (${vs.filter((f) => f.state === 'closed').map(nm).join(', ') || 'all open'} closed). Pressure reducers: ${prv.map((f) => `${nm(f)} set ${f.hyd?.set ?? 3} bar`).join('; ') || 'none'}.`,
    `Consumers: ${cons.length}, demand factor ${cfg.demandF ?? 1}${cfg.fire?.id ? `, fire flow ${cfg.fire.q} l/s at ${nm(byId[cfg.fire.id])}` : ''}.`,
  ];
  const { res, th } = aquaState();
  if (res && !res.error) {
    const nodes = Object.values(res.nodes).filter((n) => n.fid && !n.twin);
    const low = [...nodes].sort((a, b) => a.bar - b.bar).slice(0, 5), high = [...nodes].sort((a, b) => b.bar - a.bar).slice(0, 5);
    L.push(`LAST SIMULATION: inflow ${n1(res.inflow, 2)} l/s, pressure ${n1(res.min)}-${n1(res.max)} bar. Lowest: ${low.map((n) => `${nm(byId[n.fid])} ${n1(n.bar)} bar`).join(', ')}. Highest: ${high.map((n) => `${nm(byId[n.fid])} ${n1(n.bar)} bar`).join(', ')}.`);
    const fast = [...res.links].filter((l) => !l.dead).sort((a, b) => Math.abs(b.v) - Math.abs(a.v))[0];
    if (fast) L.push(`Max velocity ${n1(Math.abs(fast.v), 2)} m/s in DN${Math.round(fast.d)}.`);
    if (res.unsupplied.length) L.push(`NOT SUPPLIED (shut off): ${res.unsupplied.map((id) => nm(byId[id])).join(', ')}.`);
  }
  if (th) {
    const hot = [...th.endT].sort((a, b) => b.t - a.t).slice(0, 5);
    L.push(`WATER TEMPERATURE (supply ${cfg.tIn ?? 10} °C, scenario ${cfg.scen || 'now'}): ${n1(th.min)}-${n1(th.max)} °C. Warmest consumers: ${hot.map((e) => `${nm(byId[e.fid])} ${n1(e.t)} °C, age ${n1(e.h)} h`).join('; ')}.`);
  }
  if (cfg.clim) L.push(`Climate: air ${n1(cfg.clim.air)} °C, soil 54 cm ${n1(cfg.clim.t54)} °C, annual mean ${n1(cfg.clim.mean)} °C.`);
  return L.join('\n');
}
